
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Stars, Sparkles, CheckCircle2, InfoIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Create a lightweight logging utility that only logs in development
const isDevelopment = import.meta.env.DEV;
const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },
  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error(...args);
    }
  }
};

interface NewsletterSignupProps {
  blogPostId?: string;
  hideTitle?: boolean;
}

const NewsletterSignup = ({ blogPostId, hideTitle = false }: NewsletterSignupProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showAlreadySubscribed, setShowAlreadySubscribed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // First store in database
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({
          email,
          name,
          source_blog_id: blogPostId || null
        });
      
      if (error) {
        // If not a duplicate error
        if (!error.message?.includes('duplicate') && error.code !== '23505') {
          throw error;
        }
      }
      
      // Now also call the Mailchimp edge function
      logger.log('Calling Mailchimp edge function');
      const { error: mailchimpError, data } = await supabase.functions.invoke('mailchimp-subscribe', {
        body: { 
          email: email,
          name: name || ""
        }
      });

      logger.log('Mailchimp response:', data);
      
      if (mailchimpError) {
        logger.error('Mailchimp edge function error:', mailchimpError);
        throw new Error(mailchimpError.message || 'Failed to subscribe to newsletter');
      }
      
      // Open confirmation dialog
      setShowConfirmation(true);
      setEmail("");
      setName("");
    } catch (error: any) {
      logger.error('Error subscribing to newsletter:', error);
      
      // Check if it's a duplicate email error
      if (error.message?.includes('duplicate') || error.code === '23505') {
        // Show already subscribed dialog instead of toast
        setShowAlreadySubscribed(true);
      } else if (error.message?.includes('policy') || error.code === '42501') {
        // Row-level security policy violation - handle gracefully
        logger.error('Policy violation - continuing with confirmation experience');
        setShowConfirmation(true);
        setEmail("");
      } else {
        toast({
          title: "Subscription failed",
          description: error.message,
          variant: "destructive"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className="bg-[#34BC56] border-[#2da047] shadow-lg overflow-hidden">
        <div className="absolute top-0 right-0 -mr-4 -mt-4">
          <Sparkles className="h-20 w-20 text-[#9debb1]" />
        </div>
        <CardContent className="relative pt-6 pb-6 z-10 flex flex-col items-center text-center">
          {!hideTitle && (
            <div className="flex flex-col items-center justify-center mb-4">
              <div className="bg-white/20 rounded-full p-3 flex items-center justify-center h-16 w-16 mb-4">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2 text-white">
                Stay Updated
              </h3>
              <p className="text-white/90 max-w-lg mx-auto">
                Get exclusive content, industry insights, and advanced tips delivered directly to your inbox.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="mt-4 w-full max-w-md mx-auto flex flex-col items-center">
            <div className="w-full mb-4">
              <div className="relative mb-3">
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pr-10 bg-white/90 border-white/30 focus:border-white h-12 w-full text-left"
                />
              </div>
              <div className="relative">
                <Input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pr-10 bg-white/90 border-white/30 focus:border-white h-12 w-full text-left"
                  required
                />
                <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="h-12 px-8 font-medium bg-white text-[#34BC56] hover:bg-white/90 hover:text-[#2da047] mb-4 mx-auto"
            >
              {isSubmitting ? "Subscribing..." : "Subscribe"}
            </Button>
            
            <div className="flex items-center justify-center gap-2 text-xs text-white">
              <Stars className="h-3 w-3" />
              <p>We respect your privacy. Unsubscribe at any time.</p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Success Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-center">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <span>Successfully Subscribed!</span>
            </DialogTitle>
            <DialogDescription className="text-center pt-2">
              Thank you for subscribing to our newsletter. You'll receive exclusive content, insights, and tips delivered straight to your inbox.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button onClick={() => setShowConfirmation(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Already Subscribed Dialog */}
      <Dialog open={showAlreadySubscribed} onOpenChange={setShowAlreadySubscribed}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-center">
              <InfoIcon className="h-6 w-6 text-blue-500" />
              <span>Already Subscribed</span>
            </DialogTitle>
            <DialogDescription className="text-center pt-2">
              This email is already subscribed to our newsletter. Thank you for your continued support!
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button onClick={() => setShowAlreadySubscribed(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewsletterSignup;
